/**
 * WebhookService - Centralized webhook event management
 * Handles webhook event distribution and status tracking across the app
 *
 * The realtime WebSocket channel is optional: it only connects when a component
 * actually subscribes, it points at the API host (not the static frontend host),
 * and it gives up after a bounded number of failed attempts instead of retrying
 * forever. See resolveStreamUrl() for how the endpoint is derived.
 */

const WS_PATH = '/api/webhooks/stream';
const BASE_RECONNECT_DELAY = 5000;
const MAX_RECONNECT_DELAY = 60000;
const MAX_RECONNECT_ATTEMPTS = 6;

/**
 * Build the webhook stream URL from the configured API base.
 * Returns null when realtime webhooks are disabled or the host can't be resolved,
 * in which case the service stays in HTTP-only mode and never opens a socket.
 */
const resolveStreamUrl = () => {
  if (String(process.env.REACT_APP_WEBHOOK_WS_DISABLED).toLowerCase() === 'true') {
    return null;
  }

  const explicit = process.env.REACT_APP_WEBHOOK_WS_URL;
  if (explicit) {
    return explicit;
  }

  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) {
    return null;
  }

  try {
    // https://stage-api.quadroid.ai/api/v1 -> wss://stage-api.quadroid.ai/api/webhooks/stream
    const { protocol, host } = new URL(apiBase);
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${host}${WS_PATH}`;
  } catch {
    return null;
  }
};

class WebhookService {
  constructor() {
    this.listeners = {};
    this.agentStatuses = new Map(); // Track agent execution statuses
    this.ws = null;
    this.subscriberCount = 0;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.intentionalClose = false;
    this.streamUrl = resolveStreamUrl();
    this.streamDisabled = !this.streamUrl;
  }

  /**
   * Open the socket if it isn't already open/connecting.
   * Called lazily from subscribe() so importing this module has no side effects.
   */
  connect() {
    if (this.streamDisabled || this.reconnectTimer) {
      return;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.intentionalClose = false;

    let socket;
    try {
      socket = new WebSocket(this.streamUrl);
    } catch (error) {
      console.warn('Webhook stream unavailable, falling back to HTTP only:', error.message);
      this.streamDisabled = true;
      return;
    }

    this.ws = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      console.log('WebSocket connected for webhook events');
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebhookEvent(data);
      } catch (error) {
        console.error('Error parsing webhook event:', error);
      }
    };

    // The close handler already reports failures; an extra error log here just
    // doubles the console noise for every failed attempt.
    socket.onerror = () => {};

    socket.onclose = () => {
      this.detachSocket(socket);
      if (this.ws === socket) {
        this.ws = null;
      }
      this.scheduleReconnect();
    };
  }

  /**
   * Drop handlers from a socket we're done with so a late event from a stale
   * socket can never queue another reconnect.
   */
  detachSocket(socket) {
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
  }

  /**
   * Reconnect with exponential backoff, and stop permanently after
   * MAX_RECONNECT_ATTEMPTS so an unreachable endpoint can't spin forever.
   */
  scheduleReconnect() {
    if (this.intentionalClose || this.streamDisabled || this.subscriberCount === 0) {
      return;
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.streamDisabled = true;
      console.warn(
        `Webhook stream unreachable at ${this.streamUrl} after ${MAX_RECONNECT_ATTEMPTS} attempts. ` +
        'Realtime agent status is disabled for this session.'
      );
      return;
    }

    const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY);
    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Handle incoming webhook events and distribute to listeners
   */
  handleWebhookEvent(event) {
    const { event_type, data } = event;

    // Update agent status if applicable
    if (data?.agent_id) {
      this.updateAgentStatus(data.agent_id, event_type, data);
    }

    // Emit event to all registered listeners
    this.emit(event_type, data);
  }

  /**
   * Update agent execution status
   */
  updateAgentStatus(agentId, eventType, data) {
    const statusMap = {
      'agent_started': 'running',
      'agent_completed': 'completed',
      'agent_failed': 'failed',
    };

    const status = statusMap[eventType];
    if (status) {
      this.agentStatuses.set(agentId, {
        status,
        eventType,
        timestamp: new Date().toISOString(),
        data,
      });
    }
  }

  /**
   * Get current status of an agent
   */
  getAgentStatus(agentId) {
    return this.agentStatuses.get(agentId) || { status: 'idle' };
  }

  /**
   * Register a listener under a key and return a one-shot unsubscribe that
   * keeps the socket alive only while at least one listener is attached.
   */
  addListener(key, callback) {
    if (!this.listeners[key]) {
      this.listeners[key] = [];
    }

    this.listeners[key].push(callback);
    this.subscriberCount += 1;
    this.connect();

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;

      this.listeners[key] = (this.listeners[key] || []).filter((cb) => cb !== callback);
      this.subscriberCount = Math.max(0, this.subscriberCount - 1);

      if (this.subscriberCount === 0) {
        this.disconnect();
      }
    };
  }

  /**
   * Subscribe to webhook events
   * @param {string} eventType - The event type to listen for
   * @param {function} callback - Function to call when event occurs
   * @returns {function} Unsubscribe function
   */
  subscribe(eventType, callback) {
    return this.addListener(eventType, callback);
  }

  /**
   * Subscribe to agent-specific events
   * @param {string} agentId - The agent ID to listen for
   * @param {function} callback - Function to call when event occurs
   * @returns {function} Unsubscribe function
   */
  subscribeToAgent(agentId, callback) {
    return this.addListener(`agent_${agentId}`, callback);
  }

  /**
   * Emit event to all registered listeners
   */
  emit(eventType, data) {
    // Call generic event listeners
    if (this.listeners[eventType]) {
      this.listeners[eventType].forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in webhook listener for ${eventType}:`, error);
        }
      });
    }

    // Call agent-specific listeners if applicable
    if (data?.agent_id) {
      const agentKey = `agent_${data.agent_id}`;
      if (this.listeners[agentKey]) {
        this.listeners[agentKey].forEach((callback) => {
          try {
            callback({ eventType, ...data });
          } catch (error) {
            console.error(`Error in agent webhook listener:`, error);
          }
        });
      }
    }
  }

  /**
   * Get agent output from webhook payload
   */
  getAgentOutput(webhookData) {
    // Handle different payload structures
    if (webhookData?.output) {
      return webhookData.output;
    }

    if (webhookData?.response) {
      return webhookData.response;
    }

    if (webhookData?.data?.output) {
      return webhookData.data.output;
    }

    if (typeof webhookData === 'string') {
      try {
        const parsed = JSON.parse(webhookData);
        return parsed.response || parsed.output || webhookData;
      } catch {
        return webhookData;
      }
    }

    return null;
  }

  /**
   * Format agent output for display
   */
  formatOutput(output) {
    if (!output) return '';

    if (typeof output === 'string') {
      return output;
    }

    if (typeof output === 'object') {
      if (output.response) return output.response;
      if (output.message) return output.message;
      try {
        return JSON.stringify(output, null, 2);
      } catch {
        return String(output);
      }
    }

    return String(output);
  }

  /**
   * Handle webhook error
   */
  handleError(error, agentId, eventType) {
    const errorData = {
      agentId,
      eventType,
      error: error.message,
      timestamp: new Date().toISOString(),
    };

    this.updateAgentStatus(agentId, 'agent_failed', errorData);
    this.emit('webhook_error', errorData);
  }

  /**
   * Clear all listeners (useful for cleanup)
   */
  clearListeners() {
    this.listeners = {};
    this.subscriberCount = 0;
    this.disconnect();
  }

  /**
   * Disconnect WebSocket and cancel any pending reconnect.
   */
  disconnect() {
    this.intentionalClose = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      this.detachSocket(socket);
      socket.close();
    }

    // reconnectAttempts is deliberately NOT reset here. Only a successful open
    // clears it, so navigating between pages can't keep handing the retry loop
    // a fresh budget against an endpoint that is never going to answer.
  }
}

// Export singleton instance.
// Pinned to the window so that a re-evaluated bundle (the studio being
// re-mounted by the VAR80 host, or an HMR reload) reuses the existing instance
// instead of stacking another reconnect loop on top of the old ones.
const SINGLETON_KEY = '__quadroidWebhookService__';

if (!window[SINGLETON_KEY]) {
  window[SINGLETON_KEY] = new WebhookService();
}

const webhookService = window[SINGLETON_KEY];
export default webhookService;
